import './PlaceHeader.scss'

import { HDSDropdown, HDSIcon, HDSList, HDSListItem } from '@here/hds-react-components'
import classNames from 'classnames'
import { CloseButton } from 'components/App'
import { SortOrder } from 'components/Collections/data/model'
import { setOrder } from 'components/Collections/data/reducer'
import { useAppDispatch, useAppSelector } from 'data/store/hooks'
import { i18n } from 'lib/i18n'
import React from 'react'

export interface PlaceHeaderProps {
  title: string
  count: number
  subtitle?: string
  backgroundImageUrl: string
  onClose: () => void
}

const PlaceHeader = ({
  title,
  count,
  subtitle,
  backgroundImageUrl,
  onClose,
}: PlaceHeaderProps): JSX.Element => {
  const dispatch = useAppDispatch()
  const places = i18n('collections.place-list.places')
  const selectedSortingOption = useAppSelector((state) => state.collections.order)
  const { coordinates: userCurrentPosition } = useAppSelector((state) => state.user.currentPosition)
  const sortingOptions = Object.values(SortOrder)

  const handleOnClick = (index: number): void => {
    dispatch(setOrder(sortingOptions[index]))
  }

  const Menu = (): JSX.Element => {
    return (
      <HDSList className="Placeholder__sort-options" slot="menu" size="large">
        {sortingOptions.map((option) => (
          <HDSListItem
            key={option}
            selected={selectedSortingOption === option}
            className={classNames({
              'Placeholder__sort-option--selected': selectedSortingOption === option,
            })}
            disabled={option === SortOrder.distance && userCurrentPosition === null}
          >
            {i18n(`places.sort.${option}`)}
          </HDSListItem>
        ))}
      </HDSList>
    )
  }

  const Toggle = (): JSX.Element => {
    return (
      <div className="PlaceHeader__sort">
        <HDSIcon name="chevron-up-down" size="16" />
        {i18n(`places.sort.${selectedSortingOption}`)}
      </div>
    )
  }

  return (
    <div
      className="PlaceHeader"
      style={
        {
          backgroundImage: `url("${backgroundImageUrl}")`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        } as React.CSSProperties
      }
    >
      <div className="PlaceHeader__wrapper">
        <div className="PlaceHeader__title">
          <div className="PlaceHeader__title-description">{title}</div>
          <CloseButton
            style={{ '--hds-icon-color': 'var(--hds-ondarkcolor-primary)' } as React.CSSProperties}
            styleOnHover={
              { '--hds-icon-color': 'var(--hds-background-oncolor-hover)' } as React.CSSProperties
            }
            styleOnMouseDown={
              { '--hds-icon-color': 'var(--hds-background-oncolor-press)' } as React.CSSProperties
            }
            onClick={onClose}
          />
        </div>
        <div className="PlaceHeader__count">
          {places}: {count}
        </div>
        {Boolean(subtitle) && <div className="PlaceHeader__subtitle">{subtitle}</div>}
      </div>
      <div className="PlaceHeader__actions">
        <HDSDropdown
          className="Placeholder__dropdown"
          toggle="toggle"
          trigger={<Toggle />}
          menu={<Menu />}
          variant="subtle"
          arrow={false}
          onSelect={(itemIndex) => handleOnClick(itemIndex.index)}
        />
      </div>
    </div>
  )
}

export default PlaceHeader
